import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import {
  LinkedItemPayload,
  LinkedItemsPayload,
} from '../../types/payloads/linked-items.payloads';
import {
  LinkedItemAPIResponse,
  LinkedItemParentTable,
  LinkedItemParentTableType,
  LinkedItemSearchItemAPIResponse,
  LinkedItemSearchOption,
  LinkedItemSearchSource,
} from '../../types/responses/linked-items.responses';

const linkedItemTypeLabelMap: Record<LinkedItemParentTable, string> = {
  [LinkedItemParentTable.IMPLEMENTATION_PLAN]: 'Plan',
  [LinkedItemParentTable.COACHLOG]: 'Coachlog',
  [LinkedItemParentTable.PD_SESSION]: 'Learning',
  [LinkedItemParentTable.USER]: 'User',
  [LinkedItemParentTable.SCHOOL]: 'School',
};

const linkedItemTypeIconMap: Record<LinkedItemParentTable, string> = {
  [LinkedItemParentTable.IMPLEMENTATION_PLAN]: 'compass',
  [LinkedItemParentTable.COACHLOG]: 'card-checklist',
  [LinkedItemParentTable.PD_SESSION]: 'mortarboard',
  [LinkedItemParentTable.USER]: 'person',
  [LinkedItemParentTable.SCHOOL]: 'building',
};

function resolveLinkedItemTypeLabel(
  item: LinkedItemSearchOption,
  labelMap: Record<LinkedItemParentTable, string>
): string {
  const parentTableType = item.parent_table_type?.trim() || '';
  const normalizedParentTableType = parentTableType.toLowerCase();

  if (item.parent_table === LinkedItemParentTable.COACHLOG) {
    if (
      normalizedParentTableType ===
      LinkedItemParentTableType.COACHING_CONVERSATION
    ) {
      return 'Coaching Conversation';
    }

    if (
      normalizedParentTableType === LinkedItemParentTableType.CLASSROOM_VISIT ||
      normalizedParentTableType === LinkedItemParentTableType.OBSERVATION
    ) {
      return 'Observation and Feedback';
    }

    if (
      normalizedParentTableType === LinkedItemParentTableType.GENERAL_LOG ||
      normalizedParentTableType === LinkedItemParentTableType.UNIVERSAL_LOG
    ) {
      return 'Admin Meeting';
    }
  }

  if (item.parent_table === LinkedItemParentTable.PD_SESSION) {
    if (normalizedParentTableType === 'microlearning') {
      return 'Microlearning';
    }
    return 'Live Event';
  }

  return labelMap[item.parent_table] || 'Coachlog';
}

function resolveDefaultIcon(
  parentTable: LinkedItemParentTable,
  iconMap: Record<LinkedItemParentTable, string>
): string {
  return iconMap[parentTable] || 'link-45deg';
}

export interface LinkedItemSearchFilterOptions {
  role?: string;
  include_e2l?: boolean;
  include_district_admins?: boolean;
  district_id?: number;
}

@Injectable({
  providedIn: 'root',
})
export class LinkedItemsService {
  constructor(private apiService: APICoreService) {}

  private readonly linkedItemTypeLabelMap = linkedItemTypeLabelMap;

  private readonly linkedItemTypeIconMap = linkedItemTypeIconMap;

  getLinkedItemTypeLabel(item: LinkedItemSearchOption): string {
    return resolveLinkedItemTypeLabel(item, this.linkedItemTypeLabelMap);
  }

  getDefaultIcon(parentTable: LinkedItemParentTable): string {
    return resolveDefaultIcon(parentTable, this.linkedItemTypeIconMap);
  }

  searchItems(
    search: string,
    exclude_table: string,
    exclude_id: number,
    sources?: LinkedItemSearchSource[],
    options?: LinkedItemSearchFilterOptions
  ): Observable<LinkedItemSearchOption[]> {
    const params: {
      search: string;
      exclude_table: string;
      exclude_id: number;
      sources?: string;
      role?: string;
      include_e2l?: 0 | 1;
      include_district_admins?: 0 | 1;
      district_id?: number;
    } = {
      search,
      exclude_table,
      exclude_id,
    };

    if (sources?.length) {
      params.sources = sources.join(',');
    }

    if (options?.role) {
      params.role = options.role;
    }

    if (typeof options?.include_e2l === 'boolean') {
      params.include_e2l = options.include_e2l ? 1 : 0;
    }

    if (typeof options?.include_district_admins === 'boolean') {
      params.include_district_admins = options.include_district_admins ? 1 : 0;
    }

    if (typeof options?.district_id === 'number' && options.district_id > 0) {
      params.district_id = options.district_id;
    }

    return this.apiService
      .getRequest('growelab/crosslink/search-items', params)
      .pipe(
        map((response) =>
          LinkedItemsService.mapSearchItems(response.items || [])
        )
      );
  }

  getLinkedItems(
    target_table: string,
    target_id: number
  ): Observable<{ items: LinkedItemAPIResponse[] }> {
    return this.apiService.getRequest('growelab/crosslink', {
      target_table,
      target_id,
    });
  }

  updateLinkedItem(payload: LinkedItemPayload): Observable<LinkedItemPayload> {
    return this.apiService.postRequest('growelab/crosslink', payload);
  }

  deleteLinkedItem(id: number) {
    return this.apiService.deleteRequest(`growelab/crosslink/${id}`);
  }

  linkItems(payload: LinkedItemsPayload) {
    const normalizedPayload: LinkedItemsPayload = {
      ...payload,
      items: payload.items.map((item) => ({
        ...item,
        parent_table_type:
          item.parent_table === LinkedItemParentTable.IMPLEMENTATION_PLAN
            ? null
            : LinkedItemsService.normalizeParentTableType(
                item.parent_table_type
              ),
      })),
    };

    return this.apiService.postRequest(
      'growelab/crosslink/link-items',
      normalizedPayload
    );
  }

  private static mapSearchItems(
    items: LinkedItemSearchItemAPIResponse[]
  ): LinkedItemSearchOption[] {
    return items.map((item) => LinkedItemsService.mapSearchItem(item));
  }

  private static mapSearchItem(
    item: LinkedItemSearchItemAPIResponse
  ): LinkedItemSearchOption {
    const parentTable = LinkedItemsService.getParentTable(item.parent_table);
    const parentTableType = LinkedItemsService.normalizeParentTableType(
      item.parent_table_type
    );

    const linkUrl = LinkedItemsService.buildLinkUrl(
      parentTable,
      parentTableType,
      item.parent_id
    );

    const linkIcon = resolveDefaultIcon(parentTable, linkedItemTypeIconMap);
    const isUserOrSchool =
      parentTable === LinkedItemParentTable.USER ||
      parentTable === LinkedItemParentTable.SCHOOL;

    return {
      parent_table: parentTable,
      parent_table_type: parentTableType,
      parent_start_datetime: isUserOrSchool
        ? undefined
        : item.start_datetime ?? item.updated_at ?? 0,
      parent_end_datetime: isUserOrSchool
        ? undefined
        : item.end_datetime ?? undefined,
      parent_id: item.parent_id,
      link_icon: linkIcon,
      link_title: item.title,
      link_url: linkUrl,
    };
  }

  private static buildLinkUrl(
    parentTable: LinkedItemParentTable,
    parentTableType: LinkedItemParentTableType | null,
    parentId: number
  ): string {
    if (!parentId || parentId <= 0) {
      return '';
    }

    if (parentTable === LinkedItemParentTable.COACHLOG) {
      if (
        parentTableType === LinkedItemParentTableType.UNIVERSAL_LOG ||
        parentTableType === LinkedItemParentTableType.GENERAL_LOG
      ) {
        return `/general/log/${parentId}/info`;
      }

      if (parentTableType === LinkedItemParentTableType.COACHING_CONVERSATION) {
        return `/coaching/log/${parentId}/info`;
      }

      if (
        parentTableType === LinkedItemParentTableType.CLASSROOM_VISIT ||
        parentTableType === LinkedItemParentTableType.OBSERVATION
      ) {
        return `/observation/log/${parentId}/info`;
      }
    }

    if (parentTable === LinkedItemParentTable.PD_SESSION) {
      if (parentTableType === LinkedItemParentTableType.MICROLEARNING) {
        return `/training/session/microlearning/${parentId}`;
      }
      return `/training/session/${parentId}`;
    }

    if (parentTable === LinkedItemParentTable.IMPLEMENTATION_PLAN) {
      return `/plans/implementation-plan/${parentId}/dashboard`;
    }

    if (parentTable === LinkedItemParentTable.USER) {
      return `/portfolio/${parentId}`;
    }

    return '';
  }

  private static getParentTable(value: string): LinkedItemParentTable {
    const normalized = value.toLowerCase();

    if (normalized.includes('egrowe_coachlog')) {
      return LinkedItemParentTable.COACHLOG;
    }

    if (normalized.includes('implementation_plan')) {
      return LinkedItemParentTable.IMPLEMENTATION_PLAN;
    }

    if (normalized.includes('pd_session')) {
      return LinkedItemParentTable.PD_SESSION;
    }

    if (normalized === LinkedItemParentTable.USER) {
      return LinkedItemParentTable.USER;
    }

    if (normalized === LinkedItemParentTable.SCHOOL) {
      return LinkedItemParentTable.SCHOOL;
    }

    return LinkedItemParentTable.COACHLOG;
  }

  private static normalizeParentTableType(
    parentTableType: LinkedItemParentTableType | string | null
  ): LinkedItemParentTableType | null {
    const normalizedType = String(parentTableType || '')
      .trim()
      .toLowerCase();

    if (!normalizedType || normalizedType === 'null') {
      return null;
    }

    return normalizedType as LinkedItemParentTableType;
  }
}
